import { Box, Typography } from '@mui/material'
import { useState } from 'react'
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator'
import { format } from 'date-fns'
import { useOrder, useOrderEvents, useOrderProducts } from 'hooks/useOrders'
import theme from 'lib/theme'
import { useLocale } from 'providers/LocaleProvider'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Order, OrderEvent, OrderPart } from 'types/api/order'
import { AddNoteModal } from 'views/OrderActivity/AddNoteModal'
import {
  TimelineContainer,
  TimelineItem,
  TimelineCircle,
  AddNoteBtn,
} from './OrderActivity.styled'
import { findRefundedProduct, getProductFilteringInfo } from './utils'

export const OrderActivity = () => {
  const { selectedLocale } = useLocale()
  const { t } = useTranslation()
  const { orderId, partId } = useParams()
  const { isLoading: isLoadingOrder, order } = useOrder(orderId as string)
  const { isLoading, error, events } = useOrderEvents(
    orderId as string,
    partId as string
  )
  const { productInfo, isLoading: isLoadingProductNames } = useOrderProducts(
    getProductFilteringInfo(events, order)
  )
  const [modal, setModal] = useState({
    right: false,
  })
  const handleCloseModal = () => {
    setModal({ right: false })
  }

  const part =
    order && order.parts.filter((part: OrderPart) => part.id === partId)[0]
  const crc = part?.crc || ''

  const getStatusChangeMessage = (eventData: string) => {
    const statusChangeMessages = {
      '-1': t('eventMessage.pendingPayment'),
      '0': t('eventMessage.init'),
      '1': t('eventMessage.open'),
      '2': t('eventMessage.collecting'),
      '3': t('eventMessage.collected'),
      '4': t('eventMessage.delivering'),
      '5': t('eventMessage.delivered'),
      '6': t('eventMessage.cancelledStatus'),
    } as { [key: string]: string }
    return (
      statusChangeMessages[eventData] || t('eventMessage.orderStatusChange')
    )
  }

  const getActor = (actor: number) => {
    const possibleActors = {
      '-1': t('system'),
      '0': t('customer'),
      '1': t('store'),
      '2': t('support'),
      '3': t('driver'),
      '4': t('external'),
    } as { [key: string]: string }
    return possibleActors[actor] || ''
  }

  const getRefundedProductName = (productId: number, order: Order) => {
    const refundedProduct = productId
      ? findRefundedProduct(productId, order)
      : null
    return refundedProduct && refundedProduct.id
      ? productInfo.filter(
          (product) => Number(product.gtin) === refundedProduct.gtin
        )[0].name
      : ''
  }

  const getEventMessage = (event: OrderEvent) => {
    const { type, eventData } = event
    const { ean, newEan, quantity, amount, productId } = event.data
    const product =
      productInfo.length > 0
        ? productInfo.filter((product) => Number(product.gtin) === ean)[0] || ''
        : ''

    const refundedProductName = productId
      ? getRefundedProductName(productId, order)
      : ''
    const orderEvents = {
      '0': event.message || '',
      '1': t('eventMessage.failure'),
      '2': t('eventMessage.restore'),
      '3': t('eventMessage.driverAcknowledged'),
      '4': t('eventMessage.driverReleased'),
      '7': t('eventMessage.productAdded', { product: product.name || '' }),
      '8': t('eventMessage.productRemoved'),
      '9': t('eventMessage.slotChanged'),
      '10': getStatusChangeMessage(eventData as string),
      '12': t('eventMessage.pickingFailed', { product: product.name || '' }),
      '13': t('eventMessage.pickingSubstitute', {
        product1: product.name || '',
        product2: newEan,
      }),
      '14': t('eventMessage.quantityChange', {
        product: product.name || '',
        quantity,
      }),
      '15': t('eventMessage.pickingSuccess', { product: product.name || '' }),
      '16': t('eventMessage.pickingReopened', {
        product: product.name || '',
      }),
      '17': t('eventMessage.orderPrinted'),
      '28': t('eventMessage.productReturns'),
      '29': t('eventMessage.productAmountChanged'),
      '30': t('eventMessage.orderEdited'),
      '31': t('eventMessage.orderCreated'),
      '32': t('eventMessage.refundOpen', {
        product: refundedProductName,
        amount: amount?.toFixed(2),
      }),
      '33': t('eventMessage.refundPending', {
        product: refundedProductName,
        amount: amount?.toFixed(2),
      }),
      '34': t('eventMessage.refundSuccess', {
        product: refundedProductName,
        amount: amount?.toFixed(2),
      }),
      '35': t('eventMessage.refundFailed', {
        product: refundedProductName,
        amount: amount?.toFixed(2),
      }),
    } as { [key: string]: string }
    return orderEvents[type] || ''
  }

  const getDetailedMessage = (
    message: string,
    type: number,
    eventData: string | undefined
  ) => {
    // no message or message already displayed or redundant open status message
    if (!message || type === 0 || eventData === '1') {
      return ''
    }
    return message
  }

  if (error) return <h1>Error</h1>
  if (isLoading || isLoadingProductNames || isLoadingOrder)
    return <LoadingIndicator />
  return (
    <Box m={4}>
      <Typography variant='h2' sx={{ fontWeight: 'bold', fontSize: '12px' }}>
        {t('activity')}
      </Typography>
      {/* separating last event to prevent timeline from stretching over last circle */}
      <TimelineContainer>
        {events?.length > 0 &&
          events.slice(0, -1).map((event) => {
            const eventMessage = getEventMessage(event)
            const detailedMessage = getDetailedMessage(
              event.message,
              event.type,
              event.eventData
            )
            const actor = getActor(event.sender)
            const date = format(new Date(event.createdAt), 'dd-MM-yyyy HH:mm', {
              locale: selectedLocale,
            })
            return (
              <TimelineItem key={event.id}>
                <Box height={theme.spacing(7)}>
                  <Typography variant='body2'>{eventMessage}</Typography>
                  <Typography>{detailedMessage}</Typography>
                  <Typography
                    variant='body2'
                    sx={{ color: theme.palette.text.secondary }}>
                    {date} - {actor}
                  </Typography>
                  {event.fasUserId && (
                    <Typography
                      variant='body2'
                      sx={{ color: theme.palette.text.secondary }}>
                      {t('personnelId')}: {event.fasUserId}
                    </Typography>
                  )}
                </Box>
                <TimelineCircle />
              </TimelineItem>
            )
          })}
      </TimelineContainer>
      {events.length > 0 && (
        <TimelineItem>
          <Box height={theme.spacing(7)}>
            <Typography variant='body2'>
              {getEventMessage(events[events.length - 1])}
            </Typography>
            {events[events.length - 1].data.message && (
              <Typography>{events[events.length - 1].data.message}</Typography>
            )}
            <Typography
              variant='body2'
              sx={{ color: theme.palette.text.secondary }}>
              {format(
                new Date(events[events.length - 1].createdAt),
                'dd-MM-yyyy HH:mm',
                { locale: selectedLocale }
              )}{' '}
              - {getActor(events[events.length - 1].sender)}
            </Typography>
            {events[events.length - 1].fasUserId && (
              <Typography
                variant='body2'
                sx={{ color: theme.palette.text.secondary }}>
                {t('personnelId')}: {events[events.length - 1].fasUserId}
              </Typography>
            )}
          </Box>
          <TimelineCircle />
        </TimelineItem>
      )}
      <AddNoteBtn onClick={() => setModal({ right: true })}>
        {t('addNote')}
      </AddNoteBtn>
      <AddNoteModal
        modal={modal}
        handleCloseModal={handleCloseModal}
        orderId={orderId as string}
        partId={partId as string}
        crc={crc}
      />
    </Box>
  )
}
